/**
 * 全库消息全文检索。
 *
 * 当前走 ILIKE 而不是 `msg_text_fts` GIN 索引：该索引用的是 `simple` 分词配置，
 * PostgreSQL 默认不带中文分词，对中文查询等同于整串匹配，命不中。
 * extensions.sql 已记录上线前切 zhparser 的方案，届时这里换成 tsquery。
 */

import { Alert, Input, Select, Space, Typography } from "antd";
import { Link, useLocation } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { RelativeTime, StatusTag } from "../../components/format";
import { ConversationTabs } from "./ConversationsPage";

const FILTERS = ["q", "role", "relationshipId"];

interface SearchRow {
  id: string;
  conversationId: string;
  conversationTitle: string | null;
  relationshipId: string;
  sequence: number;
  role: string;
  formKey: string | null;
  isFallback: boolean;
  text: string | null;
  createdAt: string | null;
}

export function MessageSearchPage() {
  const { searchParams, setParam } = useTableParams(FILTERS);
  const location = useLocation();
  const q = searchParams.get("q");

  return (
    <>
      <ConversationTabs active={location.pathname} />
      {!q && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="输入关键词开始检索"
          description="在 13 万条消息里按子串匹配。中文分词（zhparser）上线前，短词命中率会偏高，请配合角色与形态筛选。"
        />
      )}
      {q && (
        <DataTable<SearchRow>
          path="/api/v1/admin/conversations/search"
          filterKeys={FILTERS}
          toolbar={<SearchToolbar />}
          columns={[
            { title: "序号", dataIndex: "sequence", width: 70, align: "right" },
            { title: "角色", dataIndex: "role", width: 90, render: (v) => <StatusTag value={v} /> },
            { title: "形态", dataIndex: "formKey", width: 100, render: (v) => <StatusTag value={v} /> },
            {
              title: "内容",
              dataIndex: "text",
              render: (v: string | null) => <Typography.Text>{v || "—"}</Typography.Text>,
            },
            {
              title: "会话",
              dataIndex: "conversationId",
              width: 100,
              render: (v: string) => <Link to={`/conversations/${v}`}>{v.slice(-8)}</Link>,
            },
            {
              title: "关系",
              dataIndex: "relationshipId",
              width: 100,
              render: (v: string) => <Link to={`/users/${v}`}>{v.slice(-8)}</Link>,
            },
            { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
          ]}
        />
      )}
      {!q && <SearchToolbar />}
    </>
  );

  function SearchToolbar() {
    return (
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          style={{ width: 360 }}
          placeholder="消息内容关键词"
          defaultValue={searchParams.get("q") ?? ""}
          onSearch={(v) => setParam("q", v)}
          enterButton="检索"
        />
        <Select
          allowClear
          style={{ width: 140 }}
          placeholder="消息角色"
          value={searchParams.get("role") ?? undefined}
          onChange={(v) => setParam("role", v)}
          options={[
            { value: "user", label: "user 用户" },
            { value: "assistant", label: "assistant 角色" },
            { value: "system", label: "system 系统" },
          ]}
        />
      </Space>
    );
  }
}
